/**
 * The golden round's fire.
 *
 * The owner asked for "fire animation or something", and the requirement he
 * gave it is the useful part: the golden round has to read as an event FROM
 * ACROSS THE ROOM. So this is a shape, not an emoji — an emoji renders at
 * whatever size and colour the platform's font decides, looks different on
 * Android and iOS, and cannot be tinted to match the pot.
 *
 * Drawn as two nested SVG paths, an outer body and an inner core, each with its
 * own flicker phase. Everything animated here is `transform` and `opacity` on a
 * composited element, so a phone spends no layout on it while the globe is also
 * spinning. `prefers-reduced-motion` stops both flickers dead (index.css) and
 * leaves a perfectly legible static flame, which is why the shape carries the
 * meaning and the motion is only ever garnish.
 */

export interface FlameProps {
  /** Rendered size in pixels. The paths scale; the stroke does not exist. */
  readonly size?: number;
  readonly className?: string;
}

export function Flame({ size = 20, className }: FlameProps): React.JSX.Element {
  return (
    <span
      className={`gg-flame${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" width={size} height={size} focusable="false">
        <path
          className="gg-flame-body"
          d="M12 1.5c.9 3.1.2 5.2-1.6 7-2.3 2.3-3.9 4-3.9 7A5.6 5.6 0 0 0 12 22.5a5.6 5.6 0 0 0 5.5-5.7c0-2.4-1-4-2.2-5.6-.5 1-1.2 1.6-2 1.9.7-3.2-.2-6.9-1.3-11.6Z"
        />
        <path
          className="gg-flame-core"
          d="M12 12.4c1.4 1.3 2.2 2.6 2.2 4.1A2.3 2.3 0 0 1 12 19a2.3 2.3 0 0 1-2.2-2.5c0-1.6.9-2.7 2.2-4.1Z"
        />
      </svg>
    </span>
  );
}

/**
 * A row of flames. Used where the golden round needs to shout rather than
 * mention — the arm card, and the top of the ladder.
 */
export function FlameRow({
  count = 3,
  size = 18,
}: {
  count?: number;
  size?: number;
}): React.JSX.Element {
  return (
    <span className="gg-flame-row" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <Flame key={i} size={size} className={`gg-flame-p${i % 3}`} />
      ))}
    </span>
  );
}
